import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PhotoCamera as PhotoCameraIcon,
  CloudUpload as CloudUploadIcon,
  Article as ArticleIcon,
  Announcement as AnnouncementIcon,
  Sports as ActivityIcon,
  Home as HomeIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Link as LinkIcon,
  Download as DownloadIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import SecretarySidebar from '../../components/SecretarySidebar';
import axios from 'axios';

// Configuration d'Axios pour gérer l'expiration des tokens
// Note: Cet intercepteur ne devrait être utilisé que dans ce composant
// Pour éviter les conflits, on vérifie la route actuelle avant de rediriger
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || 
        error.response?.data?.message?.includes('jwt expired') || 
        error.response?.data?.message?.includes('TokenExpiredError')) {
      // Vérifier si on est déjà sur une page de connexion
      const currentPath = window.location.pathname;
      if (currentPath.includes('/login') || currentPath.includes('/secretary-login')) {
        return Promise.reject(error);
      }
      
      // Nettoyer le token expiré
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      
      // Rediriger vers la page de connexion seulement si on n'est pas déjà en train de se connecter
      if (!currentPath.includes('/login') && !currentPath.includes('/secretary-login')) {
        window.location.href = '/secretary-login';
      }
    }
    return Promise.reject(error);
  }
);

// Interfaces TypeScript
interface ActivityItem {
  id: number;
  title: string;
  description?: string;
  image_url: string;
  alt_text?: string;
  order_index: number;
  category?: string;
}

interface ActivitySection {
  id: number;
  name: string;
  title: string;
  description?: string;
  section_type: 'vie_academique' | 'moments_forts';
  order_index: number;
  items: ActivityItem[];
}

interface BlogArticle {
  id: number;
  title: string;
  excerpt: string;
  content?: string;
  image_url: string;
  alt_text?: string;
  author: string;
  is_published: boolean;
  is_featured: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
  category?: string;
}

interface SchoolNews {
  id: number;
  title: string;
  description: string;
  image_url: string;
  alt_text?: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

interface HomeActivityConfig {
  id: number;
  activity_id: number;
  display_order: number;
  is_visible: boolean;
  custom_title?: string;
  custom_description?: string;
  custom_image_url?: string;
  original_title?: string;
  original_description?: string;
  original_image_url?: string;
  alt_text?: string;
  activity?: ActivityItem;
}

interface PresentationContent {
  [sectionName: string]: {
    [contentKey: string]: {
      value: string;
      type: string;
      order: number;
    };
  };
}

interface PresentationListItem {
  id: number;
  section_name: string;
  item_title: string;
  item_description: string;
  order_index: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const DynamicPagesManagement = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [activitySections, setActivitySections] = useState<ActivitySection[]>([]);
  const [blogArticles, setBlogArticles] = useState<BlogArticle[]>([]);
  const [schoolNews, setSchoolNews] = useState<SchoolNews[]>([]);
  const [homeActivities, setHomeActivities] = useState<ActivityItem[]>([]);
  const [homeActivityConfig, setHomeActivityConfig] = useState<HomeActivityConfig[]>([]);
  const [allActivities, setAllActivities] = useState<ActivityItem[]>([]);
  const [presentationContent, setPresentationContent] = useState<PresentationContent>({});
  const [presentationListItems, setPresentationListItems] = useState<PresentationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // États pour les dialogs
  const [openActivityDialog, setOpenActivityDialog] = useState(false);
  const [openBlogDialog, setOpenBlogDialog] = useState(false);
  const [openNewsDialog, setOpenNewsDialog] = useState(false);
  const [openHomeActivityDialog, setOpenHomeActivityDialog] = useState(false);
  const [openPresentationDialog, setOpenPresentationDialog] = useState(false);
  const [openPresentationListItemDialog, setOpenPresentationListItemDialog] = useState(false);
  const [selectedActivitySection, setSelectedActivitySection] = useState<ActivitySection | null>(null);
  const [selectedBlogArticle, setBlogArticle] = useState<BlogArticle | null>(null);
  const [selectedNews, setSelectedNews] = useState<SchoolNews | null>(null);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [editingHomeActivity, setEditingHomeActivity] = useState<HomeActivityConfig | null>(null);
  const [editingPresentationItem, setEditingPresentationItem] = useState<PresentationListItem | null>(null);

  // États pour les formulaires
  const [activityForm, setActivityForm] = useState({
    title: '',
    description: '',
    image_url: '',
    alt_text: '',
    category: ''
  });

  const [blogForm, setBlogForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    image_url: '',
    alt_text: '',
    author: 'La Petite Académie',
    is_featured: false,
    category: ''
  });

  const [newsForm, setNewsForm] = useState({
    title: '',
    description: '',
    image_url: '',
    alt_text: ''
  });

  const [homeActivityForm, setHomeActivityForm] = useState({
    custom_title: '',
    custom_description: '',
    custom_image_url: '',
    is_visible: true,
    display_order: 1
  });

  const [presentationForm, setPresentationForm] = useState({
    section_name: '',
    content_key: '',
    content_value: '',
    content_type: 'text'
  });

  const [presentationListItemForm, setPresentationListItemForm] = useState({
    section_name: '',
    item_title: '',
    item_description: '',
    order_index: 1
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedHomeActivityFile, setSelectedHomeActivityFile] = useState<File | null>(null);
  const [selectedPresentationFile, setSelectedPresentationFile] = useState<File | null>(null);
  const [uploadMethod, setUploadMethod] = useState<'upload' | 'url'>('url');
  const [uploadMode, setUploadMode] = useState<'single' | 'bulk'>('single');
  const [homeActivityUploadMethod, setHomeActivityUploadMethod] = useState<'upload' | 'url'>('url');
  const [presentationUploadMethod, setPresentationUploadMethod] = useState<'upload' | 'url'>('url');
  const [uploading, setUploading] = useState(false);
  const [customActivityCategory, setCustomActivityCategory] = useState('');
  const [customBlogCategory, setCustomBlogCategory] = useState('');

  const HOME_PAGE_CATEGORY = "Page d'accueil";

  const DEFAULT_ACTIVITY_CATEGORIES: Record<string, string[]> = {
    vie_academique: [
      'Sorties Éducatives',
      'Culinaire',
      'Robotique',
      'Sciences',
      'Arts',
      'Langues',
      'Projets Pédagogiques',
      'Ateliers'
    ],
    moments_forts: [
      'Graduation',
      'Événements',
      'Activités Aquatiques',
      'Fêtes',
      'Cérémonies',
      'Spectacles',
      'Compétitions',
      'Sorties Récréatives'
    ],
    default: [
      'Général',
      'Éducatif',
      'Récréatif',
      'Sportif',
      'Culturel'
    ]
  };

  // Catégories dynamiques selon la section (avec retombée sur des valeurs par défaut)
  const getActivityCategories = (section?: ActivitySection | null) => {
    if (section) {
      const sectionWithItems =
        activitySections.find((s) => s.id === section.id) || section;

      const categories = new Set<string>();

      sectionWithItems.items?.forEach((item) => {
        const category = (item.category || '').trim();
        if (category && category !== HOME_PAGE_CATEGORY) {
          categories.add(category);
        }
      });

      if (categories.size > 0) {
        return Array.from(categories).sort((a, b) =>
          a.localeCompare(b, 'fr', { sensitivity: 'base' })
        );
      }

      const defaultCategories =
        DEFAULT_ACTIVITY_CATEGORIES[section.section_type as keyof typeof DEFAULT_ACTIVITY_CATEGORIES];
      if (defaultCategories) {
        return defaultCategories;
      }
    }

    return DEFAULT_ACTIVITY_CATEGORIES.default;
  };

  const activityCategories = [
    'Sorties Éducatives',
    'Culinaire', 
    'Robotique',
    'Graduation',
    'Événements',
    'Activités Aquatiques',
    'Sciences',
    'Arts',
    'Fêtes',
    'Cérémonies'
  ];

  const blogCategories = [
    'Articles Généraux',
    'Actualités',
    'Pédagogie',
    'Événements',
    'Témoignages',
    'Conseils Parents'
  ];

  // Fonction utilitaire pour gérer l'expiration des tokens
  const handleTokenExpiration = (error: any) => {
    if (error.response?.status === 401 || error.response?.data?.message?.includes('jwt expired') || error.response?.data?.message?.includes('TokenExpiredError')) {
      setError('Session expirée. Redirection vers la page de connexion...');
      localStorage.removeItem('token');
      setTimeout(() => {
        window.location.href = '/secretary-login';
      }, 2000);
      return true;
    }
    return false;
  };

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError('');

      // Vérifier si le token existe
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Session expirée. Veuillez vous reconnecter.');
        // Rediriger vers la page de connexion
        setTimeout(() => {
          window.location.href = '/secretary-login';
        }, 2000);
        return;
      }

      // D'abord charger les activités pour avoir la référence
      const activitiesResponse = await axios.get('https://lapetiteacademie.ci/api/dynamic-pages/activities-page');
      let allActivities: ActivityItem[] = [];

      if (activitiesResponse.data.status === 'success') {
        setActivitySections(activitiesResponse.data.data.sections || []);
        
        // Collecter toutes les activités de toutes les sections (en excluant celles de la catégorie "Page d'accueil")
        activitiesResponse.data.data.sections?.forEach((section: ActivitySection) => {
          if (section.items && section.items.length > 0) {
            // Filtrer les activités qui ne sont pas de la catégorie "Page d'accueil"
            const filteredItems = section.items.filter((item: ActivityItem) => 
              item.category !== 'Page d\'accueil'
            );
            allActivities.push(...filteredItems);
          }
        });
        setAllActivities(allActivities);
      }

      // Maintenant charger les autres données en parallèle
      const [articlesResponse, newsResponse, homeActivitiesResponse, presentationResponse] = await Promise.all([
        axios.get('https://lapetiteacademie.ci/api/dynamic-pages/blog/articles'),
        axios.get('https://lapetiteacademie.ci/api/dynamic-pages/news'),
        axios.get('https://lapetiteacademie.ci/api/dynamic-pages/home-activities'),
        axios.get('https://lapetiteacademie.ci/api/dynamic-pages/presentation')
      ]);

      if (articlesResponse.data.status === 'success') {
        setBlogArticles(articlesResponse.data.data || []);
      }

      if (newsResponse.data.status === 'success') {
        setSchoolNews(newsResponse.data.data || []);
      }

      if (homeActivitiesResponse.data.status === 'success') {
        const homeActivitiesData = homeActivitiesResponse.data.data || [];
        
        // Enrichir les données avec les informations complètes des activités
        const enrichedConfig = homeActivitiesData.map((config: any) => {
          // Trouver l'activité correspondante dans allActivities
          const activity = allActivities.find(act => act.id === config.activity_id);
          
          return {
            ...config,
            activity: activity ? {
              ...activity,
              // Utiliser les données personnalisées si elles existent, sinon les données originales
              title: config.custom_title || activity.title,
              description: config.custom_description || activity.description,
              image_url: config.custom_image_url || activity.image_url,
              alt_text: config.alt_text || activity.alt_text
            } : {
              id: config.activity_id,
              title: config.custom_title || config.original_title || 'Activité inconnue',
              description: config.custom_description || config.original_description,
              image_url: config.custom_image_url || config.original_image_url || '/img/pages/default.jpg',
              alt_text: config.alt_text,
              order_index: config.display_order
            }
          };
        });
        
        setHomeActivityConfig(enrichedConfig);
      }

      if (presentationResponse.data.status === 'success') {
        setPresentationContent(presentationResponse.data.data.content || {});
        // Convertir les listItems en tableau pour faciliter la gestion
        const listItemsArray: PresentationListItem[] = [];
        Object.keys(presentationResponse.data.data.listItems || {}).forEach(sectionName => {
          presentationResponse.data.data.listItems[sectionName].forEach((item: any, index: number) => {
            listItemsArray.push({
              id: index + 1, // ID temporaire pour l'affichage
              section_name: sectionName,
              item_title: item.title,
              item_description: item.description,
              order_index: item.order,
              is_active: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
          });
        });
        setPresentationListItems(listItemsArray);
      }
    } catch (error: any) {
      console.error('Erreur lors du chargement des données:', error);
      
      // Gérer l'expiration du token
      if (!handleTokenExpiration(error)) {
        setError('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imageUrl: string) => {
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    if (imageUrl.startsWith('/img/pages/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    
    if (imageUrl.startsWith('/uploads/pages/')) {
      return `https://lapetiteacademie.ci/api/dynamic-pages/images/${imageUrl.split('/').pop()}`;
    }
    
    if (imageUrl.startsWith('/uploads/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    
    return `https://lapetiteacademie.ci${imageUrl}`;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner un fichier image valide');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('La taille du fichier ne doit pas dépasser 5MB');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const handlePresentationFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner un fichier image valide');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('La taille du fichier ne doit pas dépasser 5MB');
        return;
      }
      setSelectedPresentationFile(file);
      setError('');
    }
  };

  const handleDownloadImage = (imageUrl: string) => {
    try {
      // Extraire le nom du fichier de l'URL
      const filename = imageUrl.split('/').pop();
      if (filename) {
        // Créer un lien de téléchargement
        const downloadUrl = `https://lapetiteacademie.ci/api/dynamic-pages/download/${filename}`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      setError('Erreur lors du téléchargement de l\'image');
    }
  };

  // ==================== GESTION DES ACTIVITÉS ====================

  const handleAddActivityItem = (section: ActivitySection, mode: 'single' | 'bulk' = 'single') => {
    setSelectedActivitySection(section);
    setEditingItem(null);
    setUploadMode(mode);
    setActivityForm({
      title: '',
      description: '',
      image_url: '',
      alt_text: '',
      category: ''
    });
    setCustomActivityCategory('');
    setSelectedFile(null);
    setSelectedFiles([]);
    setUploadMethod('upload');
    setOpenActivityDialog(true);
  };

  const handleMultipleFilesSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) {
        setError(`Le fichier "${file.name}" n'est pas une image valide`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError(`Le fichier "${file.name}" dépasse la limite de 5MB`);
        return;
      }
      validFiles.push(file);
    }
    setSelectedFiles(validFiles);
    setError('');
  };

  const handleBulkUploadSubmit = async () => {
    if (!selectedActivitySection) {
      setError('Section requise');
      return;
    }
    if (selectedFiles.length === 0) {
      setError('Veuillez sélectionner au moins une image');
      return;
    }
    const finalCategory = customActivityCategory || activityForm.category;
    if (!finalCategory) {
      setError('Veuillez choisir une catégorie');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      selectedFiles.forEach(file => formData.append('images', file));
      formData.append('category', finalCategory);

      const response = await axios.post(
        `https://lapetiteacademie.ci/api/dynamic-pages/activities-page/sections/${selectedActivitySection.id}/items/upload-multiple`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.status === 'success') {
        setSuccess(`${selectedFiles.length} image(s) ajoutée(s) avec succès dans "${finalCategory}"`);
        setOpenActivityDialog(false);
        setSelectedFiles([]);
        loadAllData();
      }
    } catch (error: any) {
      console.error('Erreur lors de l\'upload multiple:', error);
      if (!handleTokenExpiration(error)) {
        setError(error.response?.data?.message || 'Erreur lors de l\'upload');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleEditActivityItem = (section: ActivitySection, item: ActivityItem) => {
    setSelectedActivitySection(section);
    setEditingItem(item);
    setActivityForm({
      title: item.title,
      description: item.description || '',
      image_url: item.image_url,
      alt_text: item.alt_text || '',
      category: item.category || ''
    });
    setCustomActivityCategory('');
    setSelectedFile(null);
    setUploadMethod('url');
    setOpenActivityDialog(true);
  };

  const handleSubmitActivityItem = async () => {
    if (!selectedActivitySection) {
      setError('Section requise');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': uploadMethod === 'upload' ? 'multipart/form-data' : 'application/json'
      };

      let imageUrl = activityForm.image_url;

      if (uploadMethod === 'upload' && selectedFile) {
        const formData = new FormData();
        formData.append('image', selectedFile);
        // Toujours envoyer le titre (même vide, car il est optionnel)
        formData.append('title', activityForm.title || '');
        if (activityForm.description) formData.append('description', activityForm.description);
        if (activityForm.alt_text) formData.append('alt_text', activityForm.alt_text);
        formData.append('category', customActivityCategory || activityForm.category || '');

        const endpoint = editingItem
          ? `https://lapetiteacademie.ci/api/dynamic-pages/activities-page/items/${editingItem.id}/upload`
          : `https://lapetiteacademie.ci/api/dynamic-pages/activities-page/sections/${selectedActivitySection.id}/items/upload`;

        const response = editingItem
          ? await axios.put(endpoint, formData, { headers })
          : await axios.post(endpoint, formData, { headers });

        if (response.data.status === 'success') {
          setSuccess(editingItem ? 'Élément mis à jour avec succès' : 'Élément ajouté avec succès');
          setOpenActivityDialog(false);
          loadAllData();
        }
      } else {
        const data = {
          title: activityForm.title || (editingItem ? editingItem.title : ''),
          description: activityForm.description || '',
          image_url: imageUrl,
          alt_text: activityForm.alt_text || '',
          category: customActivityCategory || activityForm.category || ''
        };

        console.log('📤 Données envoyées au backend:', data);
        console.log('📤 Formulaire:', activityForm);
        console.log('📤 Image URL:', imageUrl);

        const endpoint = editingItem
          ? `https://lapetiteacademie.ci/api/dynamic-pages/activities-page/items/${editingItem.id}`
          : `https://lapetiteacademie.ci/api/dynamic-pages/activities-page/sections/${selectedActivitySection.id}/items`;

        const response = editingItem
          ? await axios.put(endpoint, data, { headers })
          : await axios.post(endpoint, data, { headers });

        console.log('📥 Réponse du backend:', response.data);

        if (response.data.status === 'success') {
          setSuccess(editingItem ? 'Élément mis à jour avec succès' : 'Élément ajouté avec succès');
          setOpenActivityDialog(false);
          loadAllData();
        }
      }
    } catch (error: any) {
      console.error('Erreur lors de la soumission:', error);
      
      if (!handleTokenExpiration(error)) {
        setError(error.response?.data?.message || 'Erreur lors de la soumission');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteActivityItem = async (itemId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `https://lapetiteacademie.ci/api/dynamic-pages/activities-page/items/${itemId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setSuccess('Élément supprimé avec succès');
      loadAllData();
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      
      if (!handleTokenExpiration(error)) {
        setError(error.response?.data?.message || 'Erreur lors de la suppression');
      }
    }
  };

  // ==================== GESTION DU BLOG ====================

  const handleAddBlogArticle = () => {
    setEditingItem(null);
    setBlogForm({
      title: '',
      excerpt: '',
      content: '',
      image_url: '',
      alt_text: '',
      author: 'La Petite Académie',
      is_featured: false,
      category: ''
    });
    setCustomBlogCategory('');
    setSelectedFile(null);
    setUploadMethod('url');
    setOpenBlogDialog(true);
  };

  const handleEditBlogArticle = (article: BlogArticle) => {
    setEditingItem(article);
    setBlogForm({
      title: article.title,
      excerpt: article.excerpt,
      content: article.content || '',
      image_url: article.image_url,
      alt_text: article.alt_text || '',
      author: article.author,
      is_featured: article.is_featured,
      category: article.category || ''
    });
    setCustomBlogCategory('');
    setSelectedFile(null);
    setUploadMethod('url');
    setOpenBlogDialog(true);
  };

  const handleSubmitBlogArticle = async () => {
    if (!blogForm.title || !blogForm.excerpt) {
      setError('Titre et extrait requis');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': uploadMethod === 'upload' ? 'multipart/form-data' : 'application/json'
      };

      let imageUrl = blogForm.image_url;

      if (uploadMethod === 'upload' && selectedFile) {
        const formData = new FormData();
        formData.append('image', selectedFile);
        Object.keys(blogForm).forEach(key => {
          if (key !== 'image_url') {
            formData.append(key, (blogForm as any)[key]);
          }
        });
        formData.append('category', customBlogCategory || blogForm.category || '');

        const endpoint = editingItem
          ? `https://lapetiteacademie.ci/api/dynamic-pages/blog/articles/${editingItem.id}/upload`
          : `https://lapetiteacademie.ci/api/dynamic-pages/blog/articles/upload`;

        const response = editingItem
          ? await axios.put(endpoint, formData, { headers })
          : await axios.post(endpoint, formData, { headers });

        if (response.data.status === 'success') {
          setSuccess(editingItem ? 'Article mis à jour avec succès' : 'Article ajouté avec succès');
          setOpenBlogDialog(false);
          loadAllData();
        }
      } else {
        const data = { 
          ...blogForm, 
          image_url: imageUrl || blogForm.image_url,
          category: customBlogCategory || blogForm.category || ''
        };

        console.log('📤 Données envoyées au backend:', data);

        // Vérifier que toutes les valeurs requises sont présentes
        if (!data.title || !data.excerpt || !data.image_url) {
          setError('Titre, extrait et image sont requis');
          setUploading(false);
          return;
        }

        const endpoint = editingItem
          ? `https://lapetiteacademie.ci/api/dynamic-pages/blog/articles/${editingItem.id}`
          : `https://lapetiteacademie.ci/api/dynamic-pages/blog/articles`;

        const response = editingItem
          ? await axios.put(endpoint, data, { headers })
          : await axios.post(endpoint, data, { headers });

        if (response.data.status === 'success') {
          setSuccess(editingItem ? 'Article mis à jour avec succès' : 'Article ajouté avec succès');
          setOpenBlogDialog(false);
          loadAllData();
        }
      }
    } catch (error: any) {
      console.error('Erreur lors de la soumission:', error);
      
      if (!handleTokenExpiration(error)) {
        setError(error.response?.data?.message || 'Erreur lors de la soumission');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteBlogArticle = async (articleId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `https://lapetiteacademie.ci/api/dynamic-pages/blog/articles/${articleId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setSuccess('Article supprimé avec succès');
      loadAllData();
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      
      if (!handleTokenExpiration(error)) {
        setError(error.response?.data?.message || 'Erreur lors de la suppression');
      }
    }
  };

  // ==================== GESTION DES ACTUALITÉS ====================

  const handleAddNews = () => {
    setEditingItem(null);
    setNewsForm({
      title: '',
      description: '',
      image_url: '',
      alt_text: ''
    });
    setSelectedFile(null);
    setUploadMethod('url');
    setOpenNewsDialog(true);
  };

  const handleEditNews = (news: SchoolNews) => {
    setEditingItem(news);
    setNewsForm({
      title: news.title,
      description: news.description,
      image_url: news.image_url,
      alt_text: news.alt_text || ''
    });
    setSelectedFile(null);
    setUploadMethod('url');
    setOpenNewsDialog(true);
  };

  const handleSubmitNews = async () => {
    if (!newsForm.title || !newsForm.description) {
      setError('Titre et description requis');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': uploadMethod === 'upload' ? 'multipart/form-data' : 'application/json'
      };

      let imageUrl = newsForm.image_url;

      if (uploadMethod === 'upload' && selectedFile) {
        const formData = new FormData();
        formData.append('image', selectedFile);
        Object.keys(newsForm).forEach(key => {
          if (key !== 'image_url') {
            formData.append(key, (newsForm as any)[key]);
          }
        });

        const endpoint = editingItem
          ? `https://lapetiteacademie.ci/api/dynamic-pages/news/${editingItem.id}/upload`
          : `https://lapetiteacademie.ci/api/dynamic-pages/news/upload`;

        const response = editingItem
          ? await axios.put(endpoint, formData, { headers })
          : await axios.post(endpoint, formData, { headers });

        if (response.data.status === 'success') {
          setSuccess(editingItem ? 'Actualité mise à jour avec succès' : 'Actualité ajoutée avec succès');
          setOpenNewsDialog(false);
          loadAllData();
        }
      } else {
        const data = { ...newsForm, image_url: imageUrl };

        const endpoint = editingItem
          ? `https://lapetiteacademie.ci/api/dynamic-pages/news/${editingItem.id}`
          : `https://lapetiteacademie.ci/api/dynamic-pages/news`;

        const response = editingItem
          ? await axios.put(endpoint, data, { headers })
          : await axios.post(endpoint, data, { headers });

        if (response.data.status === 'success') {
          setSuccess(editingItem ? 'Actualité mise à jour avec succès' : 'Actualité ajoutée avec succès');
          setOpenNewsDialog(false);
          loadAllData();
        }
      }
    } catch (error: any) {
      console.error('Erreur lors de la soumission:', error);
      
      if (!handleTokenExpiration(error)) {
        setError(error.response?.data?.message || 'Erreur lors de la soumission');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteNews = async (newsId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette actualité ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `https://lapetiteacademie.ci/api/dynamic-pages/news/${newsId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setSuccess('Actualité supprimée avec succès');
      loadAllData();
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      
      if (!handleTokenExpiration(error)) {
        setError(error.response?.data?.message || 'Erreur lors de la suppression');
      }
    }
  };

  // ==================== GESTION DES ACTIVITÉS PAGE D'ACCUEIL ====================

  const handleToggleActivityVisibility = async (configId: number, currentVisibility: boolean) => {
    if (!configId) {
      setError('ID de configuration manquant');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `https://lapetiteacademie.ci/api/dynamic-pages/home-activities/${configId}`,
        { is_visible: !currentVisibility },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setSuccess('Visibilité mise à jour avec succès');
      loadAllData();
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour de la visibilité:', error);
      
      if (!handleTokenExpiration(error)) {
        setError(error.response?.data?.message || 'Erreur lors de la mise à jour de la visibilité');
      }
    }
  };

  const handleUpdateActivityOrder = (activityId: number, newOrder: number) => {
    setHomeActivityConfig(prev => {
      const updated = [...prev];
      const currentIndex = updated.findIndex(config => config.activity_id === activityId);
      const targetIndex = newOrder - 1;
      
      if (currentIndex !== -1 && targetIndex >= 0 && targetIndex < updated.length) {
        // Réorganiser l'ordre
        const [movedItem] = updated.splice(currentIndex, 1);
        updated.splice(targetIndex, 0, movedItem);
        
        // Mettre à jour les ordres
        return updated.map((config, index) => ({
          ...config,
          display_order: index + 1
        }));
      }
      return prev;
    });
  };

  const handleAddActivityToHome = async (activity: ActivityItem) => {
    if (homeActivityConfig.length >= 4) {
      setError('Maximum 4 activités peuvent être affichées sur la page d\'accueil');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `https://lapetiteacademie.ci/api/dynamic-pages/home-activities`,
        {
          activity_id: activity.id,
          is_visible: true
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setSuccess('Activité ajoutée à la page d\'accueil avec succès');
      loadAllData();
    } catch (error: any) {
      console.error('Erreur lors de l\'ajout de l\'activité:', error);
      
      if (!handleTokenExpiration(error)) {
        setError(error.response?.data?.message || 'Erreur lors de l\'ajout de l\'activité');
      }
    }
  };

  const handleRemoveActivityFromHome = async (configId: number) => {
    if (!configId) {
      setError('ID de configuration manquant');
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette activité de la page d\'accueil ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `https://lapetiteacademie.ci/api/dynamic-pages/home-activities/${configId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setSuccess('Activité supprimée de la page d\'accueil avec succès');
      loadAllData();
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      
      if (!handleTokenExpiration(error)) {
        setError(error.response?.data?.message || 'Erreur lors de la suppression');
      }
    }
  };

  const handleRemoveAllActivitiesFromHome = async () => {
    if (homeActivityConfig.length === 0) {
      setError('Aucune activité à supprimer');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer TOUTES les ${homeActivityConfig.length} activités de la page d'accueil ? Cette action est irréversible.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Supprimer toutes les activités une par une
      const deletePromises = homeActivityConfig.map(config => 
        axios.delete(
          `https://lapetiteacademie.ci/api/dynamic-pages/home-activities/${config.id}`,
          { headers }
        )
      );

      await Promise.all(deletePromises);

      setSuccess(`${homeActivityConfig.length} activités supprimées avec succès. Vous pouvez maintenant ajouter vos propres activités.`);
      loadAllData();
    } catch (error: any) {
      console.error('Erreur lors de la suppression de toutes les activités:', error);
      
      if (!handleTokenExpiration(error)) {
        setError(error.response?.data?.message || 'Erreur lors de la suppression des activités');
      }
    }
  };

  const handleEditHomeActivity = (config: HomeActivityConfig) => {
    setEditingHomeActivity(config);
    setHomeActivityForm({
      custom_title: config.custom_title || config.activity?.title || '',
      custom_description: config.custom_description || config.activity?.description || '',
      custom_image_url: config.custom_image_url || config.activity?.image_url || '',
      is_visible: config.is_visible,
      display_order: config.display_order
    });
    setSelectedHomeActivityFile(null);
    setHomeActivityUploadMethod('url');
    setOpenHomeActivityDialog(true);
  };

  const handleAddNewHomeActivity = () => {
    setEditingHomeActivity(null);
    setHomeActivityForm({
      custom_title: '',
      custom_description: '',
      custom_image_url: '',
      is_visible: true,
      display_order: homeActivityConfig.length + 1
    });
    setSelectedHomeActivityFile(null);
    setHomeActivityUploadMethod('url');
    setOpenHomeActivityDialog(true);
  };

  const handleSubmitHomeActivity = async () => {
    if (!homeActivityForm.custom_title) {
      setError('Titre requis');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      let imageUrl = homeActivityForm.custom_image_url;

      // Gérer l'upload de fichier si nécessaire
      if (homeActivityUploadMethod === 'upload' && selectedHomeActivityFile) {
        const formData = new FormData();
        formData.append('image', selectedHomeActivityFile);
        
        const uploadResponse = await axios.post(
          'https://lapetiteacademie.ci/api/dynamic-pages/upload',
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (uploadResponse.data.status === 'success') {
          imageUrl = uploadResponse.data.data.imageUrl;
        } else {
          throw new Error(uploadResponse.data.message || 'Erreur lors de l\'upload');
        }
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (editingHomeActivity) {
        // Mise à jour d'une activité existante
        await axios.put(
          `https://lapetiteacademie.ci/api/dynamic-pages/home-activities/${editingHomeActivity.id}`,
          {
            custom_title: homeActivityForm.custom_title,
            custom_description: homeActivityForm.custom_description,
            custom_image_url: imageUrl,
            is_visible: homeActivityForm.is_visible,
            display_order: homeActivityForm.display_order
          },
          { headers }
        );
        setSuccess('Activité mise à jour avec succès');
      } else {
        // Création d'une nouvelle activité directement pour la page d'accueil
        // Créer l'activité directement dans la table home_activities_config sans passer par les sections académiques
        await axios.post(
          `https://lapetiteacademie.ci/api/dynamic-pages/home-activities`,
          {
            custom_title: homeActivityForm.custom_title,
            custom_description: homeActivityForm.custom_description,
            custom_image_url: imageUrl,
            display_order: homeActivityForm.display_order,
            is_visible: homeActivityForm.is_visible
          },
          { headers }
        );
        
        setSuccess('Nouvelle activité créée et ajoutée à la page d\'accueil avec succès');
      }

      setOpenHomeActivityDialog(false);
      setSelectedHomeActivityFile(null);
      setHomeActivityUploadMethod('url');
      loadAllData();
    } catch (error: any) {
      console.error('Erreur lors de la soumission:', error);
      
      if (!handleTokenExpiration(error)) {
        setError(error.response?.data?.message || 'Erreur lors de la soumission');
      }
    } finally {
      setUploading(false);
    }
  };

  // ==================== RENDU DES COMPOSANTS ====================

  // ==================== GESTION DE LA PAGE PRÉSENTATION ====================

  const handleAddPresentationContent = () => {
    setEditingItem(null);
    setPresentationForm({
      section_name: '',
      content_key: '',
      content_value: '',
      content_type: 'text'
    });
    setSelectedPresentationFile(null);
    setPresentationUploadMethod('url');
    setOpenPresentationDialog(true);
  };

  const handleEditPresentationContent = (sectionName: string, contentKey: string) => {
    setEditingItem({ sectionName, contentKey });
    setPresentationForm({
      section_name: sectionName,
      content_key: contentKey,
      content_value: presentationContent[sectionName]?.[contentKey]?.value || '',
      content_type: presentationContent[sectionName]?.[contentKey]?.type || 'text'
    });
    setSelectedPresentationFile(null);
    setPresentationUploadMethod('url');
    setOpenPresentationDialog(true);
  };

  const handleSubmitPresentationContent = async () => {
    if (!presentationForm.section_name || !presentationForm.content_key) {
      setError('Section et clé requis');
      return;
    }

    // Si c'est une image et qu'on utilise l'upload, vérifier qu'un fichier est sélectionné
    if (presentationForm.content_type === 'image' && presentationUploadMethod === 'upload' && !selectedPresentationFile) {
      setError('Veuillez sélectionner un fichier image');
      return;
    }

    // Si c'est une image et qu'on utilise l'URL, vérifier qu'une URL est fournie
    if (presentationForm.content_type === 'image' && presentationUploadMethod === 'url' && !presentationForm.content_value) {
      setError('Veuillez fournir une URL d\'image');
      return;
    }

    // Si ce n'est pas une image, vérifier qu'une valeur est fournie
    if (presentationForm.content_type !== 'image' && !presentationForm.content_value) {
      setError('Valeur de contenu requise');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      let contentValue = presentationForm.content_value;

      // Gérer l'upload de fichier si nécessaire
      if (presentationForm.content_type === 'image' && presentationUploadMethod === 'upload' && selectedPresentationFile) {
        const formData = new FormData();
        formData.append('image', selectedPresentationFile);
        
        const uploadResponse = await axios.post(
          'https://lapetiteacademie.ci/api/dynamic-pages/upload',
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (uploadResponse.data.status === 'success') {
          contentValue = uploadResponse.data.data.imageUrl;
        } else {
          throw new Error(uploadResponse.data.message || 'Erreur lors de l\'upload');
        }
      }

      await axios.put(
        'https://lapetiteacademie.ci/api/dynamic-pages/presentation/content',
        {
          section_name: presentationForm.section_name,
          content_key: presentationForm.content_key,
          content_value: contentValue,
          content_type: presentationForm.content_type
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setSuccess('Contenu mis à jour avec succès');
      setOpenPresentationDialog(false);
      setSelectedPresentationFile(null);
      setPresentationUploadMethod('url');
      loadAllData();
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour:', error);
      
      if (!handleTokenExpiration(error)) {
        setError(error.response?.data?.message || 'Erreur lors de la mise à jour');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleAddPresentationListItem = () => {
    setEditingPresentationItem(null);
    setPresentationListItemForm({
      section_name: '',
      item_title: '',
      item_description: '',
      order_index: 1
    });
    setOpenPresentationListItemDialog(true);
  };

  const handleEditPresentationListItem = (item: PresentationListItem) => {
    setEditingPresentationItem(item);
    setPresentationListItemForm({
      section_name: item.section_name,
      item_title: item.item_title,
      item_description: item.item_description,
      order_index: item.order_index
    });
    setOpenPresentationListItemDialog(true);
  };

  const handleSubmitPresentationListItem = async () => {
    if (!presentationListItemForm.section_name || !presentationListItemForm.item_title) {
      setError('Section et titre requis');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      if (editingPresentationItem) {
        await axios.put(
          `https://lapetiteacademie.ci/api/dynamic-pages/presentation/list-items/${editingPresentationItem.id}`,
          {
            item_title: presentationListItemForm.item_title,
            item_description: presentationListItemForm.item_description,
            order_index: presentationListItemForm.order_index
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setSuccess('Élément mis à jour avec succès');
      } else {
        await axios.post(
          'https://lapetiteacademie.ci/api/dynamic-pages/presentation/list-items',
          {
            section_name: presentationListItemForm.section_name,
            item_title: presentationListItemForm.item_title,
            item_description: presentationListItemForm.item_description,
            order_index: presentationListItemForm.order_index
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setSuccess('Élément ajouté avec succès');
      }

      setOpenPresentationListItemDialog(false);
      loadAllData();
    } catch (error: any) {
      console.error('Erreur lors de la soumission:', error);
      
      if (!handleTokenExpiration(error)) {
        setError(error.response?.data?.message || 'Erreur lors de la soumission');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePresentationListItem = async (itemId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `https://lapetiteacademie.ci/api/dynamic-pages/presentation/list-items/${itemId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setSuccess('Élément supprimé avec succès');
      loadAllData();
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      
      if (!handleTokenExpiration(error)) {
        setError(error.response?.data?.message || 'Erreur lors de la suppression');
      }
    }
  };

  const renderPresentationTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color="primary.main">
          Gestion de la page Présentation
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddPresentationContent}
            size="small"
          >
            Ajouter du contenu
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddPresentationListItem}
            size="small"
          >
            Ajouter un élément de liste
          </Button>
        </Box>
      </Box>
      
      <Typography variant="body1" color="text.secondary" mb={3}>
        Gérez le contenu de la page Présentation : titres, descriptions, images et listes (missions, valeurs).
      </Typography>

      {/* Sections de contenu */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          Contenu des sections
        </Typography>
        
        <Grid container spacing={3}>
          {Object.keys(presentationContent).map((sectionName) => (
            <Grid item xs={12} key={sectionName}>
              <Card sx={{ 
                p: 3, 
                position: 'relative',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    bgcolor: 'primary.main', 
                    borderRadius: '50%', 
                    mr: 2 
                  }} />
                  <Typography variant="h6" sx={{ 
                    textTransform: 'capitalize',
                    fontWeight: 700,
                    color: 'primary.main'
                  }}>
                    {sectionName}
                  </Typography>
                </Box>
                
                {/* Organiser le contenu en deux colonnes : texte à gauche, images à droite */}
                <Grid container spacing={3}>
                  {/* Colonne de gauche : contenu texte */}
                  <Grid item xs={12} md={8}>
                    <Grid container spacing={2}>
                      {Object.keys(presentationContent[sectionName])
                        .filter(contentKey => presentationContent[sectionName][contentKey].type !== 'image')
                        .map((contentKey) => (
                        <Grid item xs={12} key={contentKey}>
                          <Box sx={{ 
                            p: 2,
                            bgcolor: '#f8f9fa',
                            borderRadius: 2,
                            border: '1px solid #e9ecef',
                            position: 'relative'
                          }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="body2" fontWeight={700} color="text.primary">
                                {contentKey}:
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleEditPresentationContent(sectionName, contentKey)}
                                sx={{
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  '&:hover': { 
                                    bgcolor: 'primary.dark',
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ 
                              lineHeight: 1.6,
                              wordBreak: 'break-word'
                            }}>
                              {presentationContent[sectionName][contentKey].value}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                  
                  {/* Colonne de droite : images */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ 
                      position: 'sticky',
                      top: 20,
                      bgcolor: '#f8f9fa',
                      borderRadius: 2,
                      p: 2,
                      border: '1px solid #e9ecef'
                    }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                        Images de la section
                      </Typography>
                      
                      {Object.keys(presentationContent[sectionName])
                        .filter(contentKey => presentationContent[sectionName][contentKey].type === 'image')
                        .map((contentKey) => (
                        <Box key={contentKey} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" fontWeight={600} color="text.primary">
                              {contentKey}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleEditPresentationContent(sectionName, contentKey)}
                              sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                '&:hover': { 
                                  bgcolor: 'primary.dark',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          
                          <Box sx={{ 
                            position: 'relative',
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '1px solid #ddd'
                          }}>
                            <img
                              src={getImageUrl(presentationContent[sectionName][contentKey].value)}
                              alt={`Image ${contentKey}`}
                              style={{
                                width: '100%',
                                height: '200px',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                              onError={(e) => {
                                e.currentTarget.src = 'https://via.placeholder.com/400x200?text=Image+non+disponible';
                              }}
                            />
                          </Box>
                          
                          <Typography variant="caption" color="text.secondary" sx={{ 
                            display: 'block',
                            mt: 1,
                            wordBreak: 'break-word',
                            fontSize: '0.75rem'
                          }}>
                            {presentationContent[sectionName][contentKey].value}
                          </Typography>
                        </Box>
                      ))}
                      
                      {Object.keys(presentationContent[sectionName])
                        .filter(contentKey => presentationContent[sectionName][contentKey].type === 'image').length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          textAlign: 'center',
                          py: 2,
                          fontStyle: 'italic'
                        }}>
                          Aucune image pour cette section
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Éléments de liste */}
      <Box>
        <Typography variant="h6" fontWeight={600} mb={2}>
          Éléments de liste (Missions, Valeurs)
        </Typography>
        
        <Grid container spacing={2}>
          {presentationListItems.map((item: PresentationListItem, index: number) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ p: 2, position: 'relative' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {item.item_title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Section: {item.section_name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {item.item_description}
                </Typography>
                
                <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleEditPresentationListItem(item)}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeletePresentationListItem(item.id)}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                    }}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );

  const renderActivityTab = () => (
    <Box>
      <Typography variant="h5" fontWeight={700} color="primary.main" mb={3}>
        Gestion de la page Activités
      </Typography>

      {activitySections.map((section) => (
        <Accordion key={section.id} sx={{ mb: 2, borderRadius: 2 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              bgcolor: section.section_type === 'vie_academique' ? '#4CAF5010' : '#FF980010',
              borderRadius: 2,
              '&:hover': { 
                bgcolor: section.section_type === 'vie_academique' ? '#4CAF5020' : '#FF980020' 
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ActivityIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                {section.title}
              </Typography>
              <Chip
                label={`${section.items.length} élément${section.items.length > 1 ? 's' : ''}`}
                size="small"
                color="primary"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleAddActivityItem(section, 'single')}
              >
                Ajouter une image
              </Button>
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => handleAddActivityItem(section, 'bulk')}
                color="secondary"
              >
                Upload multiple (catégorie)
              </Button>
            </Box>

            {section.items.length > 0 ? (
              <Grid container spacing={2}>
                {section.items.map((item) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                    <Card sx={{ position: 'relative', borderRadius: 2 }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={getImageUrl(item.image_url)}
                        alt={item.alt_text || item.title}
                        sx={{ objectFit: 'cover' }}
                        onError={(e) => {
                          e.currentTarget.src = `https://picsum.photos/400/300?random=${item.id}`;
                        }}
                      />
                      
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="h6" sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
                          {item.title}
                        </Typography>
                        {item.category && (
                          <Chip
                            label={item.category}
                            color="info"
                            size="small"
                            variant="outlined"
                            sx={{ mb: 1, fontSize: 10 }}
                          />
                        )}
                        {item.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                            {item.description.substring(0, 100)}...
                          </Typography>
                        )}
                      </CardContent>
                      
                      <Box sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        gap: 0.5
                      }}>
                        <IconButton
                          onClick={() => handleEditActivityItem(section, item)}
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                          }}
                          color="primary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDownloadImage(item.image_url)}
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                          }}
                          color="info"
                          size="small"
                          title="Télécharger l'image"
                        >
                          <DownloadIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteActivityItem(item.id)}
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                          }}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                Aucun élément dans cette section
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );

  const renderBlogTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color="primary.main">
          Gestion du Blog
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddBlogArticle}
        >
          Nouvel article
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Titre</TableCell>
              <TableCell>Auteur</TableCell>
              <TableCell>Catégorie</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {blogArticles.map((article) => (
              <TableRow key={article.id}>
                <TableCell>
                  <Box sx={{ width: 60, height: 40, overflow: 'hidden', borderRadius: 1 }}>
                    <img
                      src={getImageUrl(article.image_url)}
                      alt={article.alt_text || article.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        e.currentTarget.src = `https://picsum.photos/400/300?random=${article.id}`;
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {article.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {article.excerpt.substring(0, 80)}...
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{article.author}</Typography>
                </TableCell>
                <TableCell>
                  {article.category && (
                    <Chip
                      label={article.category}
                      color="info"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={article.is_published ? 'Publié' : 'Brouillon'}
                    color={article.is_published ? 'success' : 'default'}
                    size="small"
                  />
                  {article.is_featured && (
                    <Chip
                      label="À la une"
                      color="primary"
                      size="small"
                      sx={{ ml: 0.5 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(article.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    onClick={() => handleEditBlogArticle(article)}
                    color="primary"
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDownloadImage(article.image_url)}
                    color="info"
                    size="small"
                    title="Télécharger l'image"
                  >
                    <DownloadIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteBlogArticle(article.id)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderNewsTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color="primary.main">
          Gestion des Actualités
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNews}
        >
          Nouvelle actualité
        </Button>
      </Box>

      <Grid container spacing={3}>
        {schoolNews.map((news) => (
          <Grid item xs={12} md={6} lg={4} key={news.id}>
            <Card sx={{ position: 'relative', borderRadius: 2 }}>
              <CardMedia
                component="img"
                height="200"
                image={getImageUrl(news.image_url)}
                alt={news.alt_text || news.title}
                sx={{ objectFit: 'cover' }}
                onError={(e) => {
                  e.currentTarget.src = `https://picsum.photos/400/300?random=${news.id}`;
                }}
              />
              
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {news.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {news.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(news.created_at).toLocaleDateString()}
                </Typography>
              </CardContent>
              
              <Box sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                display: 'flex',
                gap: 0.5
              }}>
                <IconButton
                  onClick={() => handleEditNews(news)}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                  }}
                  color="primary"
                  size="small"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleDeleteNews(news.id)}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
                  }}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderHomeTab = () => (
    <Box>
      <Typography variant="h5" fontWeight={700} color="primary.main" mb={3}>
        Gestion de la section "Activités" - Page d'accueil
      </Typography>
      
      <Typography variant="body1" color="text.secondary" mb={3}>
        Gérez quelles activités apparaissent dans la section "Découvrez nos dernières activités en images" 
        sur la page d'accueil. Maximum 4 activités.
      </Typography>



      {/* Activités actuellement sélectionnées */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Activités affichées sur la page d'accueil ({homeActivityConfig.filter(c => c.is_visible).length}/4)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {homeActivityConfig.length >= 4 && (
              <Typography variant="body2" color="warning.main" sx={{ alignSelf: 'center', mr: 1 }}>
                Limite atteinte
              </Typography>
            )}
            
            
            
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddNewHomeActivity}
              disabled={homeActivityConfig.length >= 4}
              size="small"
            >
              Nouvelle activité
            </Button>
          </Box>
        </Box>
        
        <Grid container spacing={2}>
          {homeActivityConfig
            .sort((a, b) => a.display_order - b.display_order)
            .map((config) => (
            <Grid item xs={12} sm={6} md={3} key={config.id}>
              <Card sx={{ 
                position: 'relative', 
                borderRadius: 2,
                opacity: config.is_visible ? 1 : 0.6,
                border: config.is_visible ? '2px solid #4CAF50' : '2px solid #ccc'
              }}>
                <CardMedia
                  component="img"
                  height="150"
                  image={getImageUrl(config.custom_image_url || config.activity?.image_url || '')}
                  alt={config.activity?.alt_text || config.custom_title || config.activity?.title}
                  sx={{ objectFit: 'cover' }}
                />
                
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: 12, mb: 1 }}>
                    #{config.display_order} - {config.custom_title || config.activity?.title || 'Activité personnalisée'}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditHomeActivity(config)}
                      color="primary"
                      title="Modifier l'activité"
                    >
                      <EditIcon />
                    </IconButton>
                    
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActivityVisibility(config.id, config.is_visible)}
                      color={config.is_visible ? 'success' : 'default'}
                      title={config.is_visible ? 'Masquer' : 'Afficher'}
                    >
                      {config.is_visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                    
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveActivityFromHome(config.id)}
                      color="error"
                      title="Supprimer de la page d'accueil"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
        <SecretarySidebar />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography>Chargement...</Typography>
        </Box>
      </Box>
    );
  }

  // Afficher un message d'information si la session a expiré
  if (error && error.includes('Session expirée')) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
        <SecretarySidebar />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h5" color="error" textAlign="center">
            Session Expirée
          </Typography>
          <Typography variant="body1" textAlign="center" color="text.secondary">
            Votre session a expiré. Vous allez être redirigé vers la page de connexion...
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="contained" 
              onClick={() => window.location.href = '/secretary-login'}
              sx={{ mr: 2 }}
            >
              Se connecter maintenant
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => window.location.reload()}
            >
              Actualiser la page
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <SecretarySidebar />
      <Box sx={{ flex: 1, p: 3 }}>
        <Typography variant="h3" fontWeight={900} color="primary.main" mb={4} sx={{ letterSpacing: 1 }}>
          Gestion des Pages Dynamiques
        </Typography>

        <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 64,
                fontSize: '1rem',
                fontWeight: 600
              }
            }}
          >
            <Tab
              icon={<HomeIcon />}
              iconPosition="start"
              label="Page d'accueil"
            />
            <Tab
              icon={<ActivityIcon />}
              iconPosition="start"
              label="Page Activités"
            />
            <Tab
              icon={<ArticleIcon />}
              iconPosition="start"
              label="Blog"
            />
            <Tab
              icon={<AnnouncementIcon />}
              iconPosition="start"
              label="Actualités Blog"
            />
            <Tab
              icon={<InfoIcon />}
              iconPosition="start"
              label="Page Présentation"
            />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {currentTab === 0 && renderHomeTab()}
            {currentTab === 1 && renderActivityTab()}
            {currentTab === 2 && renderBlogTab()}
            {currentTab === 3 && renderNewsTab()}
            {currentTab === 4 && renderPresentationTab()}
          </Box>
        </Paper>

        {/* Dialog pour les activités */}
        <Dialog
          open={openActivityDialog}
          onClose={() => setOpenActivityDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 4 } }}
        >
          <DialogTitle sx={{ fontWeight: 900, color: 'primary.main', fontSize: 24 }}>
            {editingItem
              ? 'Modifier l\'élément'
              : uploadMode === 'bulk'
              ? `Upload multiple – ${selectedActivitySection?.title}`
              : `Ajouter une image – ${selectedActivitySection?.title}`}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {uploadMode === 'bulk' ? (
              /* ===== MODE BULK UPLOAD ===== */
              <Grid container spacing={2}>
                {/* Catégorie obligatoire */}
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Catégorie *</InputLabel>
                    <Select
                      value={activityForm.category}
                      label="Catégorie *"
                      onChange={(e) => {
                        setActivityForm({ ...activityForm, category: e.target.value });
                        if (e.target.value !== 'custom') setCustomActivityCategory('');
                      }}
                    >
                      <MenuItem value="">Sélectionner une catégorie</MenuItem>
                      {/* Catégorie par défaut demandée */}
                      <MenuItem value="Sorties">Sorties</MenuItem>
                      {getActivityCategories(selectedActivitySection)
                        .filter(c => c !== 'Sorties')
                        .map((category) => (
                          <MenuItem key={category} value={category}>{category}</MenuItem>
                        ))}
                      <MenuItem value="custom">+ Nouvelle catégorie</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {activityForm.category === 'custom' && (
                  <Grid item xs={12}>
                    <TextField
                      label="Nouvelle catégorie"
                      value={customActivityCategory}
                      onChange={(e) => setCustomActivityCategory(e.target.value)}
                      fullWidth
                      placeholder="Saisissez le nom de la catégorie"
                    />
                  </Grid>
                )}

                {/* Zone de dépôt de fichiers multiples */}
                <Grid item xs={12}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="activity-bulk-upload"
                    type="file"
                    multiple
                    onChange={handleMultipleFilesSelect}
                  />
                  <label htmlFor="activity-bulk-upload">
                    <Box
                      sx={{
                        border: '2px dashed',
                        borderColor: selectedFiles.length > 0 ? 'success.main' : 'primary.main',
                        borderRadius: 3,
                        p: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: selectedFiles.length > 0 ? 'success.50' : 'primary.50',
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: 'primary.100' }
                      }}
                    >
                      <CloudUploadIcon sx={{ fontSize: 48, color: selectedFiles.length > 0 ? 'success.main' : 'primary.main', mb: 1 }} />
                      <Typography variant="h6" fontWeight={600}>
                        {selectedFiles.length > 0
                          ? `${selectedFiles.length} photo(s) sélectionnée(s)`
                          : 'Cliquez pour sélectionner plusieurs photos'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Formats acceptés : JPG, PNG, WEBP, GIF · Max 5 MB par fichier · Jusqu'à 100 photos
                      </Typography>
                    </Box>
                  </label>
                </Grid>

                {/* Aperçu miniatures */}
                {selectedFiles.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight={600} mb={1}>
                      Aperçu ({selectedFiles.length} photo{selectedFiles.length > 1 ? 's' : ''})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {selectedFiles.map((file, idx) => (
                        <Box
                          key={idx}
                          component="img"
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          sx={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            ) : (
              /* ===== MODE SINGLE (existant) ===== */
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Titre (optionnel)"
                    value={activityForm.title}
                    onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Description (optionnel)"
                    value={activityForm.description}
                    onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Catégorie</InputLabel>
                    <Select
                      value={activityForm.category}
                      label="Catégorie"
                      onChange={(e) => {
                        setActivityForm({ ...activityForm, category: e.target.value });
                        if (e.target.value !== 'custom') setCustomActivityCategory('');
                      }}
                    >
                      <MenuItem value="">Sélectionner une catégorie</MenuItem>
                      <MenuItem value="Sorties">Sorties</MenuItem>
                      {getActivityCategories(selectedActivitySection)
                        .filter(c => c !== 'Sorties')
                        .map((category) => (
                          <MenuItem key={category} value={category}>{category}</MenuItem>
                        ))}
                      <MenuItem value="custom">+ Nouvelle catégorie</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {activityForm.category === 'custom' && (
                  <Grid item xs={12}>
                    <TextField
                      label="Nouvelle catégorie"
                      value={customActivityCategory}
                      onChange={(e) => setCustomActivityCategory(e.target.value)}
                      fullWidth
                      placeholder="Saisissez le nom de la nouvelle catégorie"
                      helperText="Cette catégorie sera créée et pourra être réutilisée"
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Méthode d'ajout d'image</InputLabel>
                    <Select
                      value={uploadMethod}
                      label="Méthode d'ajout d'image"
                      onChange={(e) => setUploadMethod(e.target.value as 'upload' | 'url')}
                    >
                      <MenuItem value="url">URL d'image</MenuItem>
                      <MenuItem value="upload">Upload de fichier</MenuItem>
                    </Select>
                  </FormControl>

                  {uploadMethod === 'upload' ? (
                    <Box>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="activity-image-upload"
                        type="file"
                        onChange={handleFileSelect}
                      />
                      <label htmlFor="activity-image-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<PhotoCameraIcon />}
                          fullWidth
                          sx={{ py: 2, borderStyle: 'dashed' }}
                        >
                          {selectedFile ? selectedFile.name : 'Sélectionner une image'}
                        </Button>
                      </label>
                    </Box>
                  ) : (
                    <TextField
                      label="URL de l'image"
                      value={activityForm.image_url}
                      onChange={(e) => setActivityForm({ ...activityForm, image_url: e.target.value })}
                      fullWidth
                      required
                      placeholder="https://example.com/image.jpg"
                    />
                  )}
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Texte alternatif (optionnel)"
                    value={activityForm.alt_text}
                    onChange={(e) => setActivityForm({ ...activityForm, alt_text: e.target.value })}
                    fullWidth
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 2 }}>
            <Button
              onClick={() => setOpenActivityDialog(false)}
              variant="outlined"
              disabled={uploading}
            >
              Annuler
            </Button>
            {uploadMode === 'bulk' ? (
              <Button
                onClick={handleBulkUploadSubmit}
                variant="contained"
                color="secondary"
                startIcon={<CloudUploadIcon />}
                disabled={
                  uploading ||
                  selectedFiles.length === 0 ||
                  (!activityForm.category && !customActivityCategory) ||
                  (activityForm.category === 'custom' && !customActivityCategory)
                }
              >
                {uploading ? `Upload en cours...` : `Envoyer ${selectedFiles.length} photo(s)`}
              </Button>
            ) : (
              <Button
                onClick={handleSubmitActivityItem}
                variant="contained"
                disabled={uploading || (!activityForm.image_url && !selectedFile) || (activityForm.category === 'custom' && !customActivityCategory)}
              >
                {uploading ? 'En cours...' : (editingItem ? 'Mettre à jour' : 'Ajouter')}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Dialog pour le blog */}
        <Dialog
          open={openBlogDialog}
          onClose={() => setOpenBlogDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 4 } }}
        >
          <DialogTitle sx={{ fontWeight: 900, color: 'primary.main', fontSize: 24 }}>
            {editingItem ? 'Modifier l\'article' : 'Nouvel article de blog'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Titre"
                  value={blogForm.title}
                  onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Extrait"
                  value={blogForm.excerpt}
                  onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                  fullWidth
                  required
                  multiline
                  rows={2}
                  helperText="Résumé de l'article qui apparaîtra sur la page blog"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Contenu complet (optionnel)"
                  value={blogForm.content}
                  onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })}
                  fullWidth
                  multiline
                  rows={6}
                  helperText="Contenu détaillé de l'article"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Auteur"
                  value={blogForm.author}
                  onChange={(e) => setBlogForm({ ...blogForm, author: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Article à la une</InputLabel>
                  <Select
                    value={blogForm.is_featured ? 'true' : 'false'}
                    label="Article à la une"
                    onChange={(e) => setBlogForm({ ...blogForm, is_featured: e.target.value === 'true' })}
                  >
                    <MenuItem value="false">Non</MenuItem>
                    <MenuItem value="true">Oui</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Catégorie</InputLabel>
                  <Select
                    value={blogForm.category}
                    label="Catégorie"
                    onChange={(e) => {
                      setBlogForm({ ...blogForm, category: e.target.value });
                      if (e.target.value !== 'custom') {
                        setCustomBlogCategory('');
                      }
                    }}
                  >
                    <MenuItem value="">Sélectionner une catégorie</MenuItem>
                    {blogCategories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                    <MenuItem value="custom">+ Nouvelle catégorie</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {blogForm.category === 'custom' && (
                <Grid item xs={12}>
                  <TextField
                    label="Nouvelle catégorie"
                    value={customBlogCategory}
                    onChange={(e) => setCustomBlogCategory(e.target.value)}
                    fullWidth
                    placeholder="Saisissez le nom de la nouvelle catégorie"
                    helperText="Cette catégorie sera créée et pourra être réutilisée"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Méthode d'ajout d'image</InputLabel>
                  <Select
                    value={uploadMethod}
                    label="Méthode d'ajout d'image"
                    onChange={(e) => setUploadMethod(e.target.value as 'upload' | 'url')}
                  >
                    <MenuItem value="url">URL d'image</MenuItem>
                    <MenuItem value="upload">Upload de fichier</MenuItem>
                  </Select>
                </FormControl>

                {uploadMethod === 'upload' ? (
                  <Box>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="blog-image-upload"
                      type="file"
                      onChange={handleFileSelect}
                    />
                    <label htmlFor="blog-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<PhotoCameraIcon />}
                        fullWidth
                        sx={{ py: 2, borderStyle: 'dashed' }}
                      >
                        {selectedFile ? selectedFile.name : 'Sélectionner une image'}
                      </Button>
                    </label>
                  </Box>
                ) : (
                  <TextField
                    label="URL de l'image"
                    value={blogForm.image_url}
                    onChange={(e) => setBlogForm({ ...blogForm, image_url: e.target.value })}
                    fullWidth
                    required
                    placeholder="https://example.com/image.jpg"
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Texte alternatif (optionnel)"
                  value={blogForm.alt_text}
                  onChange={(e) => setBlogForm({ ...blogForm, alt_text: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 2 }}>
            <Button
              onClick={() => setOpenBlogDialog(false)}
              variant="outlined"
              disabled={uploading}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSubmitBlogArticle}
              variant="contained"
              disabled={uploading || !blogForm.title || !blogForm.excerpt || (!blogForm.image_url && !selectedFile) || (blogForm.category === 'custom' && !customBlogCategory)}
            >
              {uploading ? 'En cours...' : (editingItem ? 'Mettre à jour' : 'Publier')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog pour les actualités */}
        <Dialog
          open={openNewsDialog}
          onClose={() => setOpenNewsDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 4 } }}
        >
          <DialogTitle sx={{ fontWeight: 900, color: 'primary.main', fontSize: 24 }}>
            {editingItem ? 'Modifier l\'actualité' : 'Nouvelle actualité'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Titre"
                  value={newsForm.title}
                  onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={newsForm.description}
                  onChange={(e) => setNewsForm({ ...newsForm, description: e.target.value })}
                  fullWidth
                  required
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Méthode d'ajout d'image</InputLabel>
                  <Select
                    value={uploadMethod}
                    label="Méthode d'ajout d'image"
                    onChange={(e) => setUploadMethod(e.target.value as 'upload' | 'url')}
                  >
                    <MenuItem value="url">URL d'image</MenuItem>
                    <MenuItem value="upload">Upload de fichier</MenuItem>
                  </Select>
                </FormControl>

                {uploadMethod === 'upload' ? (
                  <Box>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="news-image-upload"
                      type="file"
                      onChange={handleFileSelect}
                    />
                    <label htmlFor="news-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<PhotoCameraIcon />}
                        fullWidth
                        sx={{ py: 2, borderStyle: 'dashed' }}
                      >
                        {selectedFile ? selectedFile.name : 'Sélectionner une image'}
                      </Button>
                    </label>
                  </Box>
                ) : (
                  <TextField
                    label="URL de l'image"
                    value={newsForm.image_url}
                    onChange={(e) => setNewsForm({ ...newsForm, image_url: e.target.value })}
                    fullWidth
                    required
                    placeholder="https://example.com/image.jpg"
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Texte alternatif (optionnel)"
                  value={newsForm.alt_text}
                  onChange={(e) => setNewsForm({ ...newsForm, alt_text: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 2 }}>
            <Button
              onClick={() => setOpenNewsDialog(false)}
              variant="outlined"
              disabled={uploading}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSubmitNews}
              variant="contained"
              disabled={uploading || !newsForm.title || !newsForm.description || (!newsForm.image_url && !selectedFile)}
            >
              {uploading ? 'En cours...' : (editingItem ? 'Mettre à jour' : 'Publier')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog pour les activités de la page d'accueil */}
        <Dialog
          open={openHomeActivityDialog}
          onClose={() => setOpenHomeActivityDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 4 } }}
        >
          <DialogTitle sx={{ fontWeight: 900, color: 'primary.main', fontSize: 24 }}>
            {editingHomeActivity ? 'Modifier l\'activité de la page d\'accueil' : 'Nouvelle activité pour la page d\'accueil'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Titre personnalisé"
                  value={homeActivityForm.custom_title}
                  onChange={(e) => setHomeActivityForm({ ...homeActivityForm, custom_title: e.target.value })}
                  fullWidth
                  required
                  helperText="Titre qui apparaîtra sur la page d'accueil"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description personnalisée"
                  value={homeActivityForm.custom_description}
                  onChange={(e) => setHomeActivityForm({ ...homeActivityForm, custom_description: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  helperText="Description qui apparaîtra sur la page d'accueil (optionnel)"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  Image personnalisée
                </Typography>
                
                {/* Choix de la méthode d'upload */}
                <ToggleButtonGroup
                  value={homeActivityUploadMethod}
                  exclusive
                  onChange={(e: React.MouseEvent<HTMLElement>, value: 'upload' | 'url' | null) => value && setHomeActivityUploadMethod(value)}
                  size="small"
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value="url">
                    <LinkIcon sx={{ mr: 1 }} />
                    URL
                  </ToggleButton>
                  <ToggleButton value="upload">
                    <CloudUploadIcon sx={{ mr: 1 }} />
                    Upload
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* Champ URL */}
                {homeActivityUploadMethod === 'url' && (
                  <TextField
                    label="URL de l'image"
                    value={homeActivityForm.custom_image_url}
                    onChange={(e) => setHomeActivityForm({ ...homeActivityForm, custom_image_url: e.target.value })}
                    fullWidth
                    helperText="URL de l'image personnalisée (optionnel - utilise l'image originale si vide)"
                    placeholder="https://example.com/image.jpg"
                  />
                )}

                {/* Upload de fichier */}
                {homeActivityUploadMethod === 'upload' && (
                  <Box>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="home-activity-image-upload"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setSelectedHomeActivityFile(file);
                          // Effacer l'URL si on upload un fichier
                          setHomeActivityForm({ ...homeActivityForm, custom_image_url: '' });
                        }
                      }}
                    />
                    <label htmlFor="home-activity-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        fullWidth
                        sx={{ mb: 1 }}
                      >
                        {selectedHomeActivityFile ? 'Changer l\'image' : 'Sélectionner une image'}
                      </Button>
                    </label>
                    
                    {selectedHomeActivityFile && (
                      <Box sx={{ 
                        p: 2, 
                        border: '1px dashed #ccc', 
                        borderRadius: 1,
                        textAlign: 'center',
                        bgcolor: '#f9f9f9'
                      }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Fichier sélectionné:
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {selectedHomeActivityFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(selectedHomeActivityFile.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Ordre d'affichage</InputLabel>
                  <Select
                    value={homeActivityForm.display_order}
                    label="Ordre d'affichage"
                    onChange={(e) => setHomeActivityForm({ ...homeActivityForm, display_order: e.target.value as number })}
                  >
                    {[1, 2, 3, 4].map(num => (
                      <MenuItem key={num} value={num}>{num}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Visibilité</InputLabel>
                  <Select
                    value={homeActivityForm.is_visible ? 'true' : 'false'}
                    label="Visibilité"
                    onChange={(e) => setHomeActivityForm({ ...homeActivityForm, is_visible: e.target.value === 'true' })}
                  >
                    <MenuItem value="true">Visible</MenuItem>
                    <MenuItem value="false">Masqué</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {editingHomeActivity && editingHomeActivity.activity_id && (
                <Grid item xs={12}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: '#f5f5f5', 
                    borderRadius: 1,
                    border: '1px solid #ddd'
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Informations originales:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: 12 }}>
                      <strong>Titre original:</strong> {editingHomeActivity.activity?.title || 'N/A'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: 12 }}>
                      <strong>Description originale:</strong> {editingHomeActivity.activity?.description || 'N/A'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: 12 }}>
                      <strong>Image originale:</strong> {editingHomeActivity.activity?.image_url || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 2 }}>
            <Button
              onClick={() => setOpenHomeActivityDialog(false)}
              variant="outlined"
              disabled={uploading}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSubmitHomeActivity}
              variant="contained"
              disabled={uploading || !homeActivityForm.custom_title || (homeActivityUploadMethod === 'upload' && !selectedHomeActivityFile && !homeActivityForm.custom_image_url)}
            >
              {uploading ? 'En cours...' : (editingHomeActivity ? 'Mettre à jour' : 'Créer l\'activité')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog pour le contenu de la page Présentation */}
        <Dialog
          open={openPresentationDialog}
          onClose={() => setOpenPresentationDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 4 } }}
        >
          <DialogTitle sx={{ fontWeight: 900, color: 'primary.main', fontSize: 24 }}>
            {editingItem ? 'Modifier le contenu' : 'Ajouter du contenu'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Section"
                  value={presentationForm.section_name}
                  onChange={(e) => setPresentationForm({ ...presentationForm, section_name: e.target.value })}
                  fullWidth
                  required
                  helperText="Ex: header, introduction, vision, etc."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Clé de contenu"
                  value={presentationForm.content_key}
                  onChange={(e) => setPresentationForm({ ...presentationForm, content_key: e.target.value })}
                  fullWidth
                  required
                  helperText="Ex: title, description, image, etc."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Type de contenu</InputLabel>
                  <Select
                    value={presentationForm.content_type}
                    label="Type de contenu"
                    onChange={(e) => setPresentationForm({ ...presentationForm, content_type: e.target.value })}
                  >
                    <MenuItem value="text">Texte</MenuItem>
                    <MenuItem value="title">Titre</MenuItem>
                    <MenuItem value="description">Description</MenuItem>
                    <MenuItem value="image">Image</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                {presentationForm.content_type === 'image' ? (
                  <Box>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Méthode d'ajout d'image</InputLabel>
                      <Select
                        value={presentationUploadMethod}
                        label="Méthode d'ajout d'image"
                        onChange={(e) => setPresentationUploadMethod(e.target.value as 'upload' | 'url')}
                      >
                        <MenuItem value="url">URL d'image</MenuItem>
                        <MenuItem value="upload">Upload de fichier</MenuItem>
                      </Select>
                    </FormControl>

                    {presentationUploadMethod === 'upload' ? (
                      <Box>
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="presentation-image-upload"
                          type="file"
                          onChange={handlePresentationFileSelect}
                        />
                        <label htmlFor="presentation-image-upload">
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<PhotoCameraIcon />}
                            fullWidth
                            sx={{ py: 2, borderStyle: 'dashed' }}
                          >
                            {selectedPresentationFile ? selectedPresentationFile.name : 'Sélectionner une image'}
                          </Button>
                        </label>
                        
                        {selectedPresentationFile && (
                          <Box sx={{ 
                            p: 2, 
                            border: '1px dashed #ccc', 
                            borderRadius: 1,
                            textAlign: 'center',
                            bgcolor: '#f9f9f9',
                            mt: 1
                          }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              Fichier sélectionné:
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {selectedPresentationFile.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(selectedPresentationFile.size / 1024 / 1024).toFixed(2)} MB
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <TextField
                        label="URL de l'image"
                        value={presentationForm.content_value}
                        onChange={(e) => setPresentationForm({ ...presentationForm, content_value: e.target.value })}
                        fullWidth
                        required
                        placeholder="https://example.com/image.jpg"
                        helperText="URL de l'image à afficher"
                      />
                    )}
                  </Box>
                ) : (
                  <TextField
                    label="Valeur du contenu"
                    value={presentationForm.content_value}
                    onChange={(e) => setPresentationForm({ ...presentationForm, content_value: e.target.value })}
                    fullWidth
                    required
                    multiline
                    rows={4}
                    helperText="Le contenu à afficher (texte, description, etc.)"
                  />
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 2 }}>
            <Button
              onClick={() => setOpenPresentationDialog(false)}
              variant="outlined"
              disabled={uploading}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSubmitPresentationContent}
              variant="contained"
              disabled={
                uploading || 
                !presentationForm.section_name || 
                !presentationForm.content_key || 
                (presentationForm.content_type === 'image' && presentationUploadMethod === 'upload' && !selectedPresentationFile) ||
                (presentationForm.content_type === 'image' && presentationUploadMethod === 'url' && !presentationForm.content_value) ||
                (presentationForm.content_type !== 'image' && !presentationForm.content_value)
              }
            >
              {uploading ? 'En cours...' : (editingItem ? 'Mettre à jour' : 'Ajouter')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog pour les éléments de liste de la page Présentation */}
        <Dialog
          open={openPresentationListItemDialog}
          onClose={() => setOpenPresentationListItemDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 4 } }}
        >
          <DialogTitle sx={{ fontWeight: 900, color: 'primary.main', fontSize: 24 }}>
            {editingPresentationItem ? 'Modifier l\'élément de liste' : 'Ajouter un élément de liste'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Section</InputLabel>
                  <Select
                    value={presentationListItemForm.section_name}
                    label="Section"
                    onChange={(e) => setPresentationListItemForm({ ...presentationListItemForm, section_name: e.target.value })}
                  >
                    <MenuItem value="missions">Missions</MenuItem>
                    <MenuItem value="valeurs">Valeurs</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Ordre d'affichage"
                  type="number"
                  value={presentationListItemForm.order_index}
                  onChange={(e) => setPresentationListItemForm({ ...presentationListItemForm, order_index: parseInt(e.target.value) || 1 })}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Titre de l'élément"
                  value={presentationListItemForm.item_title}
                  onChange={(e) => setPresentationListItemForm({ ...presentationListItemForm, item_title: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={presentationListItemForm.item_description}
                  onChange={(e) => setPresentationListItemForm({ ...presentationListItemForm, item_description: e.target.value })}
                  fullWidth
                  multiline
                  rows={4}
                  helperText="Description détaillée de l'élément"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 2 }}>
            <Button
              onClick={() => setOpenPresentationListItemDialog(false)}
              variant="outlined"
              disabled={uploading}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSubmitPresentationListItem}
              variant="contained"
              disabled={uploading || !presentationListItemForm.section_name || !presentationListItemForm.item_title}
            >
              {uploading ? 'En cours...' : (editingPresentationItem ? 'Mettre à jour' : 'Ajouter')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbars pour les notifications */}
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default DynamicPagesManagement;
